import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RotateCw } from 'lucide-react';
import {
  fetchDashboardActivity,
  fetchDashboardFreshness,
  fetchDashboardReview,
  fetchDashboardSources,
  type ActivitySeries,
  type DashboardActivity,
  type DashboardFreshness,
  type DashboardReview,
  type DashboardSources,
  type QueueKey,
  type QueueTrend,
  type SeriesPoint,
} from '../api/dashboard';
import { HeaderActions } from '../components/HeaderActions';
import BreakdownTooltip from '../components/dashboard/BreakdownTooltip';
import DataFreshness from '../components/dashboard/DataFreshness';
import DataSources from '../components/dashboard/DataSources';
import { Button } from '../components/ui/button';
import { TooltipProvider } from '../components/ui/tooltip';
import { usePolling } from '../hooks/usePolling';
import { cn } from '../lib/utils';
import { fmtAgo, fmtDay, fmtDayYear, fmtInt, fmtSigned } from '../utils/format';

// Client dashboard v2.1 (BOT-451): Review, Data freshness, Activity, Data sources.
// Review and Activity are polled every 60 s, Data sources (refreshed every 10 min)
// every 2 min, Data freshness (an hourly rollup) every 5 min, only while the tab
// is visible; polls never extend the session (pollsession).

const POLL_MS = 60_000;
const SOURCES_POLL_MS = 2 * 60_000;
const FRESHNESS_POLL_MS = 5 * 60_000;

const REVIEW_PATH: Record<QueueKey, string> = {
  price: '/price-checks/review',
  home_value: '/home-value/review',
  new_items: '/new-items/review',
};

const QUEUE_LABEL: Record<QueueKey, string> = {
  price: 'Price',
  home_value: 'Home Value',
  new_items: 'New Items',
};

const H2 = 'text-xs font-semibold uppercase tracking-[0.06em] text-zinc-600';
const CARD_LABEL = 'text-[11px] font-semibold uppercase tracking-[0.04em] text-zinc-500';
const CARD = 'rounded-lg border border-border bg-background';

export default function DashboardPage() {
  const [review, setReview] = useState<DashboardReview | null>(null);
  const [activity, setActivity] = useState<DashboardActivity | null>(null);

  const load = useCallback(async (background: boolean) => {
    const [r, a] = await Promise.all([
      fetchDashboardReview({ background }),
      fetchDashboardActivity({ background }),
    ]);
    setReview(r);
    setActivity(a);
  }, []);

  const { lastUpdated, error, refreshing, refresh } = usePolling(load, POLL_MS);

  const [freshness, setFreshness] = useState<DashboardFreshness | null>(null);
  const loadFreshness = useCallback(async (background: boolean) => {
    setFreshness(await fetchDashboardFreshness({ background }));
  }, []);
  const freshnessPoll = usePolling(loadFreshness, FRESHNESS_POLL_MS);

  const [sources, setSources] = useState<DashboardSources | null>(null);
  const loadSources = useCallback(async (background: boolean) => {
    setSources(await fetchDashboardSources({ background }));
  }, []);
  const sourcesPoll = usePolling(loadSources, SOURCES_POLL_MS);

  return (
    <>
      <HeaderActions>
        <UpdatedStamp lastUpdated={lastUpdated} failed={!!error} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => { void refresh(); void freshnessPoll.refresh(); void sourcesPoll.refresh(); }}
          disabled={refreshing}
        >
          <RotateCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} aria-hidden />
          Refresh
        </Button>
      </HeaderActions>

      <div className="flex flex-col gap-7 pb-12">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
          <h1 className="text-xl font-semibold tracking-[-0.02em]">Dashboard</h1>
          <div className="ml-auto flex flex-wrap gap-2">
            {review && (
              <Chip>
                <strong className="font-semibold text-zinc-950">{fmtInt(review.waiting_total)}</strong>{' '}
                waiting for review
              </Chip>
            )}
            {freshness && (
              <>
                <Chip>
                  Price <strong className="font-semibold text-zinc-950">{fmtPct1(freshness.price.fresh_pct)}</strong> fresh
                </Chip>
                <Chip>
                  Home Value{' '}
                  <strong className="font-semibold text-zinc-950">{fmtPct1(freshness.home_value.fresh_pct)}</strong> fresh
                </Chip>
              </>
            )}
          </div>
        </div>

        {!review && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Couldn't load the dashboard. It retries every minute, or press Refresh.
          </div>
        )}

        <section aria-labelledby="h-review" className="flex flex-col gap-3">
          <h2 id="h-review" className="sr-only">Review</h2>
          {review ? <ReviewBlock review={review} /> : !error && <ReviewSkeleton />}
        </section>

        {freshnessPoll.error && !freshness ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Couldn't load Data freshness. It retries every 5 minutes, or press Refresh.
          </div>
        ) : (
          <DataFreshness data={freshness} />
        )}

        <section aria-labelledby="h-activity" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <h2 id="h-activity" className={H2}>Activity · last 7 days</h2>
            <span className="text-xs text-zinc-500">
              Changes written to NetSuite · days in US Eastern time (ET)
            </span>
          </div>
          {activity ? <ActivityBlock activity={activity} /> : !error && <ActivitySkeleton />}
        </section>

        {sourcesPoll.error && !sources ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            Couldn't load Data sources. It retries every 2 minutes, or press Refresh.
          </div>
        ) : (
          <DataSources data={sources} />
        )}
      </div>
    </>
  );
}

// ── header ────────────────────────────────────────────────────────────────────

function UpdatedStamp({ lastUpdated, failed }: { lastUpdated: Date | null; failed: boolean }) {
  // Re-render every 30 s so "Updated Xm ago" keeps moving between polls.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  if (failed && lastUpdated) {
    return (
      <span className="text-xs text-red-700" role="status">
        Couldn't refresh · retrying · data from {fmtAgo(lastUpdated, now)}
      </span>
    );
  }
  if (!lastUpdated) return null;
  return <span className="text-xs text-zinc-500">Updated {fmtAgo(lastUpdated, now)}</span>;
}

/** Header chip share: "62.4%", "100%", or "—" when there is nothing to measure. */
function fmtPct1(pct: number | null): string {
  if (pct == null) return '—';
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-[26px] items-center gap-1.5 rounded-full bg-zinc-100 px-2.5 text-xs text-zinc-700 tabular-nums">
      {children}
    </span>
  );
}

// ── Review ────────────────────────────────────────────────────────────────────

function ReviewBlock({ review }: { review: DashboardReview }) {
  const { price, home_value: hv, new_items: ni } = review.tiles;

  const priceSub =
    price.pending === 0
      ? 'Nothing waiting'
      : `${fmtInt(price.unsafe)} unsafe${price.oldest_days != null ? ` · oldest ${days(price.oldest_days)}` : ''}`;
  const hvSub =
    `Delivery time: ${fmtInt(hv.shorter)} shorter · ${fmtInt(hv.longer)} longer` +
    (hv.first_value > 0 ? ` · ${fmtInt(hv.first_value)} first value` : '');
  const niSub = `${fmtInt(ni.auto_skipped)} skipped automatically (stock rule)`;

  return (
    <div className="grid gap-4 min-[1200px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="grid gap-4 min-[900px]:grid-cols-3">
        <ReviewTile label="Price Checks · Review" count={price.pending} sub={priceSub}
                    series={price.series} trend={price.trend} to={REVIEW_PATH.price} />
        <ReviewTile label="Home Value · Review" count={hv.pending} sub={hvSub}
                    series={hv.series} trend={hv.trend} to={REVIEW_PATH.home_value} />
        <ReviewTile label="New Items · Review" count={ni.pending} sub={niSub}
                    series={ni.series} trend={ni.trend} to={REVIEW_PATH.new_items} />
      </div>
      <PendingByVendor review={review} />
    </div>
  );
}

function days(n: number) {
  if (n < 1) return 'under 1 day';
  return `${fmtInt(n)} ${n === 1 ? 'day' : 'days'}`;
}

const TREND_TONE: Record<QueueTrend['direction'], string> = {
  growing: 'text-red-700',
  stable: 'text-zinc-600',
  shrinking: 'text-green-800',
};

function trendText(trend: QueueTrend | null): string {
  if (!trend) return 'Not enough history yet';
  const span = trend.days === 1 ? '1 day' : `${trend.days} days`;
  return `Queue ${trend.direction} · ${fmtSigned(trend.delta)} in ${span}`;
}

function ReviewTile({
  label, count, sub, series, trend, to,
}: {
  label: string;
  count: number;
  sub: string;
  series: SeriesPoint[];
  trend: QueueTrend | null;
  to: string;
}) {
  const tone = trend ? TREND_TONE[trend.direction] : 'text-zinc-400';
  return (
    <div className={cn(CARD, 'flex flex-col')}>
      <Link to={to} className="flex flex-col gap-1.5 px-[18px] pb-3 pt-4 hover:text-zinc-950">
        <span className={CARD_LABEL}>{label}</span>
        <span className="text-[32px] font-semibold leading-[1.1] tracking-[-0.02em] tabular-nums">
          {fmtInt(count)}
        </span>
        <span className="text-[13px] text-zinc-600 tabular-nums">{sub}</span>
      </Link>
      <div className={cn('mt-2 flex flex-col gap-1 px-[18px] pb-3', tone)}>
        <Sparkline series={series} />
        <span className="text-xs tabular-nums">{trendText(trend)}</span>
      </div>
      <div className="mt-auto flex min-h-[44px] items-center justify-end border-t border-zinc-100 px-[18px]">
        <Link to={to} className="flex min-h-[44px] items-center gap-1.5 text-xs font-medium text-zinc-700 hover:text-zinc-950">
          Open review <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

/** 30-day queue size; a missing day breaks the line instead of being drawn as 0. */
function Sparkline({ series }: { series: SeriesPoint[] }) {
  const values = series.map(p => p.pending).filter((v): v is number => v != null);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const n = Math.max(series.length - 1, 1);
  const x = (i: number) => (i * 200) / n;
  const y = (v: number) => 36 - ((v - min) / (max - min || 1)) * 32;

  const segments: string[] = [];
  let current: string[] = [];
  series.forEach((p, i) => {
    if (p.pending == null) {
      if (current.length) segments.push(current.join(' '));
      current = [];
      return;
    }
    current.push(`${x(i).toFixed(1)},${y(p.pending).toFixed(1)}`);
  });
  if (current.length) segments.push(current.join(' '));

  return (
    <svg viewBox="0 0 200 40" width="100%" height="40" preserveAspectRatio="none"
         role="img" aria-label="Queue size, last 30 days">
      {segments.map((pts, i) => {
        // A lone day still shows: draw it as a short dash.
        const points = pts.includes(' ')
          ? pts
          : (() => {
              const [px, py] = pts.split(',').map(Number);
              return `${px - 1},${py} ${px + 1},${py}`;
            })();
        return (
          <polyline key={i} points={points} fill="none" stroke="currentColor" strokeWidth={2}
                    strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        );
      })}
    </svg>
  );
}

// Badge-style outlined fills (Review page palette).
const SEG = {
  safe: 'border-green-400 bg-green-50',
  unsafe: 'border-red-400 bg-red-50',
  neutral: 'border-zinc-300 bg-zinc-50',
} as const;

type Segment = { key: string; value: number; cls: string };

function segmentsFor(queue: QueueKey, row: DashboardReview['by_vendor'][QueueKey][number]): Segment[] {
  if (queue === 'price') {
    const r = row as DashboardReview['by_vendor']['price'][number];
    return [
      { key: 'Safe', value: r.safe, cls: SEG.safe },
      { key: 'Unsafe', value: r.unsafe, cls: SEG.unsafe },
    ];
  }
  if (queue === 'home_value') {
    const r = row as DashboardReview['by_vendor']['home_value'][number];
    return [
      { key: 'Shorter delivery', value: r.shorter, cls: SEG.safe },
      { key: 'Longer delivery', value: r.longer, cls: SEG.unsafe },
      { key: 'First value', value: r.first_value, cls: SEG.neutral },
    ];
  }
  return [{ key: 'Waiting', value: row.pending, cls: SEG.neutral }];
}

const LEGEND: Record<QueueKey, Segment[]> = {
  price: [
    { key: 'Safe', value: 0, cls: SEG.safe },
    { key: 'Unsafe', value: 0, cls: SEG.unsafe },
  ],
  home_value: [
    { key: 'Shorter delivery', value: 0, cls: SEG.safe },
    { key: 'Longer delivery', value: 0, cls: SEG.unsafe },
    { key: 'First value', value: 0, cls: SEG.neutral },
  ],
  new_items: [],
};

function reviewHref(queue: QueueKey, vendorCodes: string[]): string {
  const q = new URLSearchParams();
  for (const c of vendorCodes) q.append('vendor', c);
  return `${REVIEW_PATH[queue]}?${q.toString()}`;
}

function PendingByVendor({ review }: { review: DashboardReview }) {
  const [queue, setQueue] = useState<QueueKey>('price');
  const rows = review.by_vendor[queue];
  const top = Math.max(1, ...rows.map(r => r.pending));

  return (
    <div className={cn(CARD, 'flex flex-col gap-3 px-[18px] py-4')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3 className={CARD_LABEL}>Pending by vendor</h3>
        <div role="group" aria-label="Review type" className="ml-auto flex gap-0.5 rounded-md bg-zinc-100 p-0.5">
          {(Object.keys(QUEUE_LABEL) as QueueKey[]).map(k => (
            <button
              key={k}
              type="button"
              aria-pressed={queue === k}
              onClick={() => setQueue(k)}
              className={cn(
                'h-[26px] rounded-[5px] border px-2.5 text-xs',
                queue === k
                  ? 'border-border bg-background font-medium text-zinc-950'
                  : 'border-transparent text-zinc-600 hover:text-zinc-950',
              )}
            >
              {QUEUE_LABEL[k]}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-zinc-500">Nothing waiting for review.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rows.map(row => (
            <Link
              key={row.label}
              to={reviewHref(queue, row.vendor_codes)}
              title={`Open ${row.label} Review`}
              className="grid min-h-[26px] grid-cols-[110px_minmax(0,1fr)_44px] items-center gap-2.5 text-zinc-700 hover:text-zinc-950"
            >
              <span className="truncate text-right text-xs">{row.label}</span>
              <span className="flex h-4 gap-0.5" style={{ width: `${(row.pending / top) * 100}%` }}>
                {segmentsFor(queue, row)
                  .filter(s => s.value > 0)
                  .map(s => (
                    <span
                      key={s.key}
                      title={`${s.key}: ${fmtInt(s.value)}`}
                      className={cn('box-border rounded border', s.cls)}
                      style={{ width: `${(s.value / row.pending) * 100}%` }}
                    />
                  ))}
              </span>
              <span className="text-xs text-zinc-600 tabular-nums">{fmtInt(row.pending)}</span>
            </Link>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-600">
        {LEGEND[queue].map(s => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span className={cn('box-border h-3 w-3 rounded-[3px] border', s.cls)} />
            {s.key}
          </span>
        ))}
        <span className="text-zinc-500">Click a vendor to open its Review</span>
      </div>
    </div>
  );
}

function ReviewSkeleton() {
  return (
    <div className="grid gap-4 min-[1200px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]" aria-busy="true">
      <div className="grid gap-4 min-[900px]:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} className={cn(CARD, 'h-[236px] animate-pulse bg-zinc-50')} />)}
      </div>
      <div className={cn(CARD, 'h-[236px] animate-pulse bg-zinc-50')} />
    </div>
  );
}

// ── Activity ──────────────────────────────────────────────────────────────────

const ACTIVITY_CARDS: { key: QueueKey; title: string }[] = [
  { key: 'price', title: 'Price updates' },
  { key: 'home_value', title: 'Home Value updates' },
  { key: 'new_items', title: 'New items added' },
];

// Bar segment colors, also used for the hover card's dots.
const AUTO_DOT = { backgroundColor: '#10b981' }; // emerald-500
const MANUAL_DOT = { backgroundColor: '#3b82f6' }; // blue-500

function ActivityBlock({ activity }: { activity: DashboardActivity }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="grid gap-4 min-[900px]:grid-cols-3">
        {ACTIVITY_CARDS.map(c => (
          <ActivityCard key={c.key} title={c.title} days={activity.days} series={activity[c.key]} />
        ))}
      </div>
    </TooltipProvider>
  );
}

/** Round up to 1, 2 or 5 × 10^k so the axis label reads cleanly. */
function niceMax(v: number): number {
  if (v <= 0) return 10;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

function ActivityCard({ title, days, series }: { title: string; days: string[]; series: ActivitySeries }) {
  const top = niceMax(Math.max(0, ...series.automatic.map((a, i) => a + series.manual[i])));
  const [active, setActive] = useState<number | null>(null);
  const slot = 390 / days.length;
  const barW = Math.min(36, slot * 0.65);
  const h = (v: number) => (v / top) * 160;

  return (
    <div className={cn(CARD, 'flex flex-col gap-2 px-[18px] py-4')}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className={CARD_LABEL}>{title}</h3>
        <span className="text-xl font-semibold tabular-nums">{fmtInt(series.total)}</span>
      </div>
      <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-zinc-700 tabular-nums">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-[2px]" style={AUTO_DOT} />
          {fmtInt(series.automatic_total)} automatic
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-[2px]" style={MANUAL_DOT} />
          {fmtInt(series.manual_total)} approved manually
        </span>
      </div>
      <svg viewBox="0 0 420 190" width="100%" height="190" role="img" aria-label={`${title} per day, last 7 days`}>
        {/* The hovered day's column, shaded behind the grid and its bars. */}
        {active != null && (
          <rect data-testid="activity-day-highlight" x={30 + slot * active} y="10" width={slot} height="160" rx="4" fill="#f4f4f5" />
        )}
        <line x1="30" y1="10" x2="420" y2="10" stroke="#f4f4f5" />
        <line x1="30" y1="90" x2="420" y2="90" stroke="#f4f4f5" />
        <line x1="30" y1="170" x2="420" y2="170" stroke="#e4e4e7" />
        <text x="24" y="14" textAnchor="end" fontSize="11" fill="#71717a">{fmtInt(top)}</text>
        <text x="24" y="174" textAnchor="end" fontSize="11" fill="#71717a">0</text>
        {days.map((d, i) => {
          const a = series.automatic[i];
          const m = series.manual[i];
          const cx = 30 + slot * i + slot / 2;
          const x = cx - barW / 2;
          const ya = 170 - h(a);
          return (
            <g key={d}>
              {a > 0 && <rect x={x} y={ya} width={barW} height={h(a)} fill={AUTO_DOT.backgroundColor} />}
              {m > 0 && <rect x={x} y={ya - h(m)} width={barW} height={h(m)} fill={MANUAL_DOT.backgroundColor} />}
              <text x={cx} y="186" textAnchor="middle" fontSize="11" fill="#71717a">{fmtDay(d)}</text>
              {/* The whole day column is the hover target, so short and empty days
                  open the card too (v2.2). Radix tooltip, not <title>: the native
                  one shows only after ~1-2 s (BOT-492). The card sits right of the
                  column, top-aligned with the chart (left near the screen edge). */}
              <BreakdownTooltip
                placement="beside"
                onOpenChange={open => setActive(cur => (open ? i : cur === i ? null : cur))}
                title={fmtDayYear(d)}
                rows={[
                  { key: 'automatic', label: 'Automatic', dot: AUTO_DOT, value: a },
                  { key: 'manual', label: 'Approved manually', dot: MANUAL_DOT, value: m },
                ]}
                total={fmtInt(a + m)}
              >
                <rect data-testid="activity-day" x={30 + slot * i} y="10" width={slot} height="160" fill="transparent" />
              </BreakdownTooltip>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ActivitySkeleton() {
  return (
    <div className="grid gap-4 min-[900px]:grid-cols-3" aria-busy="true">
      {[0, 1, 2].map(i => <div key={i} className={cn(CARD, 'h-[270px] animate-pulse bg-zinc-50')} />)}
    </div>
  );
}
